import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "@intrasync/stores";

interface PdfDocument {
  id: number;
  fileName: string;
  storagePath: string;
  blobId?: string;
  blobUrl?: string;
  latestEtag?: string;
  fileSize?: number;
  status?: "active" | "archived" | "deleted";
  createdAt?: string;
  updatedAt?: string;
  metadata?: Record<string, any>;
}

export class PdfManagementStore {
  // UI State only - no entities stored here
  selectedDocumentId: string | null = null;
  filterByType: string = "all";
  searchTerm: string = "";
  viewMode: "grid" | "list" = "grid";
  isUploading = false;
  uploadError: string | null = null;
  uploadProgress = 0;

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this, { rootStore: false } as any);
  }

  // Actions - UI state only
  setSelectedDocument = (documentId: string | null) => {
    this.selectedDocumentId = documentId;
  };

  setFilterByType = (type: string) => {
    this.filterByType = type;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setViewMode = (mode: "grid" | "list") => {
    this.viewMode = mode;
  };

  setUploadProgress = (progress: number) => {
    this.uploadProgress = progress;
  };

  // Async Actions
  loadDocuments = async () => {
    try {
      await (this.rootStore as any).pdfDocumentsStore.find({
        query: {
          status: "active", // Only load active documents
          $limit: 100,
          $sort: {
            createdAt: -1,
          },
        },
      });
    } catch (error: any) {
      console.error("Error loading PDF documents:", error);
    }
  };

  uploadDocument = async (file: File, metadata: any = {}) => {
    this.isUploading = true;
    this.uploadError = null;
    this.uploadProgress = 0;

    try {
      const pdfDocumentsStore = (this.rootStore as any).pdfDocumentsStore;
      const authStore = (this.rootStore as any).authStore;

      // Step 1: Request a SAS URL from the backend (preferred method)
      const uploadData = {
        requestBlobUrl: true, // This triggers SAS URL generation
        fileName: file.name || "unknown.pdf",
        contentType: file.type || "application/pdf",
        fileSize: file.size,
        metadata: {
          ...metadata,
          uploadedBy: authStore?.user?.email || "unknown",
          uploadedAt: new Date().toISOString(),
          documentType: metadata.documentType || "general",
        },
        tags: metadata.tags || ["pdf-document"],
        // Don't send file data when requesting blob URL
      };

      console.log("Requesting SAS URL for PDF upload...");
      runInAction(() => {
        this.uploadProgress = 10;
      });

      const fileRecord = await pdfDocumentsStore.create(uploadData);

      // Step 2: Upload directly to Azure if we got a SAS URL
      if (fileRecord.uploadUrl) {
        console.log("Got SAS URL, uploading directly to Azure...");
        runInAction(() => {
          this.uploadProgress = 30;
        });

        try {
          const response = await fetch(fileRecord.uploadUrl, {
            method: "PUT",
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "Content-Type": file.type || "application/pdf",
              // Don't set Content-Length header - browser will handle it
            },
            body: file, // Send raw file, not Base64
          });

          if (!response.ok) {
            throw new Error(
              `Direct upload failed with status ${response.status}: ${response.statusText}`
            );
          }

          console.log("Upload successful!");
          runInAction(() => {
            this.uploadProgress = 90;
          });

          // Step 3: Since the document appears in the list after upload,
          // the backend may be handling confirmation automatically.
          // If the API requires explicit confirmation, it may need a custom endpoint
          // For now, we'll skip the confirmation since the upload succeeds

          console.log("Upload completed successfully!");
        } catch (uploadError: any) {
          console.error("Upload failed:", uploadError);

          // Log the error but don't try to update the document
          // as it may not support these fields

          throw uploadError;
        }
      } else {
        // Fallback: Use Base64 for smaller files if no SAS URL provided
        console.log("No SAS URL provided, using Base64 fallback...");

        const MAX_BASE64_SIZE = 10 * 1024 * 1024; // 10MB limit for base64

        if (file.size > MAX_BASE64_SIZE) {
          throw new Error(
            `File size ${(file.size / 1024 / 1024).toFixed(
              2
            )}MB exceeds maximum upload size. Direct upload may not be configured properly.`
          );
        }

        // Use the updated base64 upload method
        const document = await this.uploadWithBase64(file, metadata);
        return document;
      }

      runInAction(() => {
        this.isUploading = false;
        this.uploadProgress = 100;
      });

      // Reload documents
      await this.loadDocuments();

      return fileRecord;
    } catch (error: any) {
      runInAction(() => {
        this.isUploading = false;
        this.uploadError = error.message || "Upload failed";
        this.uploadProgress = 0;
      });
      throw error;
    }
  };

  // Fallback method for base64 upload
  private uploadWithBase64 = async (file: File, metadata: any = {}) => {
    runInAction(() => {
      this.uploadProgress = 20;
    });

    // Read file as base64
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onload = () => {
        const base64 = reader.result as string;
        const base64Data = base64.split(",")[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
    });
    reader.readAsDataURL(file);
    const base64Data = await base64Promise;

    runInAction(() => {
      this.uploadProgress = 50;
    });

    const authStore = (this.rootStore as any).authStore;

    // Create document with base64 data - using new field names and structure
    const documentData: any = {
      file: base64Data, // Changed from fileData to file as per API update
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type || "application/pdf",
      metadata: {
        ...metadata,
        uploadedBy: authStore?.user?.email || "unknown",
        uploadedAt: new Date().toISOString(),
        documentType: metadata.documentType || "general",
        name: metadata.name || file.name,
        description: metadata.description || "",
      },
      tags: metadata.tags || ["pdf-document"],
      requestBlobUrl: false, // Explicitly false for base64 upload
    };

    console.log("Uploading PDF with base64 data...");

    const document = await (this.rootStore as any).pdfDocumentsStore.create(
      documentData
    );

    runInAction(() => {
      this.isUploading = false;
      this.uploadProgress = 100;
    });

    // Reload documents
    await this.loadDocuments();

    return document;
  };

  deleteDocument = async (documentId: string | number) => {
    const pdfDocumentsStore = (this.rootStore as any).pdfDocumentsStore;

    try {
      // Use patch to mark as deleted (soft delete) like DrawingManagementStore
      // Or use remove with deleteBlob flag for hard delete
      // Check if backend supports soft delete first
      const useSoftDelete = false; // Set based on backend capability

      if (useSoftDelete) {
        // Soft delete - mark as deleted but keep record
        await pdfDocumentsStore.patch(documentId, {
          status: "deleted",
          deletedAt: new Date().toISOString(),
        });
      } else {
        // Hard delete - remove record and blob
        // Backend should handle blob deletion when deleteBlob is true
        await pdfDocumentsStore.remove(documentId, {
          deleteBlob: true, // Signal backend to clean up blob storage
        });
      }

      // Reload documents to refresh the list
      await this.loadDocuments();
    } catch (error: any) {
      console.error("Error deleting document:", error);
      throw error;
    }
  };

  downloadDocument = async (pdfDocument: PdfDocument) => {
    try {
      // Get the download URL - for public containers, direct URL works
      const downloadUrl = pdfDocument.blobUrl || (pdfDocument as any).url;

      if (!downloadUrl) {
        throw new Error("No download URL available for this document.");
      }

      console.log("Attempting download from:", downloadUrl);

      // Download the file
      const response = await fetch(downloadUrl);

      if (!response.ok) {
        // If 404, the file doesn't exist in blob storage
        if (response.status === 404) {
          console.error("File not found at:", downloadUrl);
          throw new Error(
            "The PDF file was not found in storage. The file may not have been uploaded successfully. Please try uploading it again."
          );
        }
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = pdfDocument.fileName || "document.pdf";
      a.click();
      window.URL.revokeObjectURL(url);

      console.log("Download completed successfully");
    } catch (error) {
      console.error("Error downloading document:", error);
      // Provide user-friendly error message
      if (error instanceof Error && error.message.includes("not found")) {
        alert(
          "This PDF file cannot be downloaded because it was not found in storage. Please try uploading it again."
        );
      } else {
        alert("Failed to download the PDF. Please try again.");
      }
      throw error;
    }
  };

  // Computed values - reference ServiceStore entities
  get documents() {
    return (this.rootStore as any).pdfDocumentsStore?.entities || [];
  }

  get filteredDocuments() {
    let filtered = this.documents;

    // Filter by type
    if (this.filterByType !== "all") {
      filtered = filtered.filter(
        (doc: any) => doc.metadata?.documentType === this.filterByType
      );
    }

    // Filter by search term
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (doc: any) =>
          doc.fileName?.toLowerCase().includes(search) ||
          doc.storagePath?.toLowerCase().includes(search) ||
          doc.metadata?.description?.toLowerCase().includes(search)
      );
    }

    // Sort by creation date (newest first)
    return filtered.slice().sort((a: any, b: any) => {
      const dateA = new Date(
        a.createdAt || a.metadata?.uploadedAt || 0
      ).getTime();
      const dateB = new Date(
        b.createdAt || b.metadata?.uploadedAt || 0
      ).getTime();
      return dateB - dateA;
    });
  }

  get documentTypes() {
    const types = new Set<string>();
    this.documents.forEach((doc: any) => {
      if (doc.metadata?.documentType) {
        types.add(doc.metadata.documentType);
      }
    });
    return Array.from(types);
  }

  get selectedDocument() {
    if (!this.selectedDocumentId) return null;
    return (this.rootStore as any).pdfDocumentsStore?.entities.find(
      (d: any) => d.id === this.selectedDocumentId
    );
  }

  get isLoadingDocuments() {
    return (this.rootStore as any).pdfDocumentsStore?.isFindPending || false;
  }

  get errorLoadingDocuments() {
    return (this.rootStore as any).pdfDocumentsStore?.errorOnFind || null;
  }
}
